/**
 * UI-6b Task 17 — DashboardComposer admin page.
 *
 * Drag-grid composer for role-scoped tenant dashboards. The page bundles
 * three concerns that Tasks 14–16 each shipped a chunk of:
 *
 *   - Widget library sidebar (Task 14)            — the drag source.
 *   - Composer canvas backed by DashboardRenderer — the drop target /
 *     live-preview, reusing the same renderer the supervisor landing page
 *     uses for the read-only view.
 *   - WidgetConfigDialog (Task 15)                — opened by clicking a
 *     placed widget; edits buffer locally + propagate back on Save.
 *
 * Wiring choices:
 *
 *   1. The list fetch is local (`useEffect + api.get`), NOT
 *      `useAdminResource`. The hook composes URLs as `${basePath}/${id}` for
 *      `update`/`remove`; when `basePath` already carries a query string
 *      (`/admin/dashboards?role=supervisor`), that composition produces
 *      `/admin/dashboards?role=supervisor/<id>` — broken. ContactsPage /
 *      CallbacksPage hit the same constraint and use the same local-fetch
 *      pattern (documented there).
 *
 *   2. The composer's *working layout* lives in component state separate
 *      from the saved dashboard. `editing` is the in-progress dashboard;
 *      `layout` is the unsaved draft. Save Layout PATCHes; Cancel /
 *      switching role discards. Resetting `editing` whenever the role
 *      changes prevents a stale "editing supervisor dashboard while role is
 *      agent" footgun.
 *
 *   3. Drop position (Phase 1) is computed from the placement index, not
 *      the actual drop coordinates. The spec explicitly defers
 *      drag-to-resize + precise grid placement to Phase 2 (design §4.3,
 *      §2 out-of-scope); for now we record (row = floor(i/2), col = (i%2)*6)
 *      so two widgets per row in the 12-col grid. Phase 2 may translate
 *      e.clientX/clientY into snapped grid coords.
 *
 *   4. The "+ New" affordance opens a small name-prompt dialog (not a
 *      drawer) — there's only one field. POSTing returns the row, which we
 *      immediately set as `editing` so the user can drop widgets into it.
 *
 *   5. Delete uses PrimeReact's `confirmDialog` global, same as
 *      SipTrunksPage / ContactsPage; the dialog mounts via the suite-wide
 *      `<ConfirmDialog />` at the page foot.
 *
 *   6. Unknown widget types dropped from elsewhere (e.g. an old layout
 *      JSONB or another draggable's payload) are silently ignored — the
 *      composer is best-effort and never throws for a missing manifest.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type JSX,
} from 'react'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Dialog } from 'primereact/dialog'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'

import { api } from '../../api/client.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import PageHeader from '../../components/admin/PageHeader.js'
import { useToast } from '../../components/system/ToastProvider.js'
import WidgetLibrarySidebar, {
  WIDGET_DRAG_MIME,
} from '../../components/dashboards/WidgetLibrarySidebar.js'
import DashboardRenderer from '../../components/dashboards/DashboardRenderer.js'
import WidgetConfigDialog from '../../components/dashboards/WidgetConfigDialog.js'
import { WIDGET_REGISTRY } from '../../components/dashboards/widgets/index.js'
import type {
  DashboardLayout,
  WidgetInstance,
} from '../../components/dashboards/widgets/types.js'
import type { Dashboard } from '../../hooks/useDefaultDashboard.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DashboardRole = 'supervisor' | 'agent' | 'admin'

const ROLE_OPTIONS: ReadonlyArray<{ label: string; value: DashboardRole }> = [
  { label: 'Supervisor', value: 'supervisor' },
  { label: 'Agent', value: 'agent' },
  { label: 'Admin', value: 'admin' },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Phase-1 grid-slot allocator. The 12-column grid hosts two 6-wide widgets
 * per row by default; we feed each new drop into the next slot. Phase 2
 * will replace this with snap-to-grid based on e.clientX/clientY.
 */
function nextPosition(layout: DashboardLayout): { row: number; col: number } {
  const i = layout.length
  return { row: Math.floor(i / 2), col: (i % 2) * 6 }
}

// ---------------------------------------------------------------------------
// Inner page (RequirePermission wraps this)
// ---------------------------------------------------------------------------

function DashboardComposerInner(): JSX.Element {
  const toast = useToast()

  const [role, setRole] = useState<DashboardRole>('supervisor')
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The dashboard currently open in the composer canvas + its working layout
  // draft. Both are reset whenever `role` changes — the user shouldn't be
  // able to save a "supervisor dashboard" while the agent role is active.
  const [editing, setEditing] = useState<Dashboard | null>(null)
  const [layout, setLayout] = useState<DashboardLayout>([])

  // WidgetConfigDialog state.
  const [configInstance, setConfigInstance] = useState<WidgetInstance | null>(null)

  // "+ New" name-prompt dialog state.
  const [newOpen, setNewOpen] = useState(false)
  const [newName, setNewName] = useState('')

  // Mounted guard for local fetch — same pattern as ContactsPage.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<{ dashboards: Dashboard[] }>(
        `/admin/dashboards?role=${encodeURIComponent(role)}`,
      )
      if (mounted.current) {
        setDashboards(resp.dashboards)
        setLoading(false)
      }
    } catch (e) {
      if (mounted.current) {
        setError(e instanceof Error ? e.message : 'Failed to load dashboards.')
        setLoading(false)
      }
    }
  }, [role])

  useEffect(() => {
    void load()
  }, [load])

  // Role change discards in-progress edits — see header comment.
  useEffect(() => {
    setEditing(null)
    setLayout([])
  }, [role])

  // -------------------------------------------------------------------------
  // Drop handler — parses the custom MIME payload, looks up the manifest,
  // appends a new WidgetInstance to the working layout. Unknown widget
  // types are ignored silently (e.g. stale layouts, other drag sources).
  // -------------------------------------------------------------------------

  function handleDragOver(e: DragEvent<HTMLDivElement>): void {
    // preventDefault is required for the browser to allow the subsequent drop.
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }

  function handleDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault()
    const widgetType = e.dataTransfer?.getData(WIDGET_DRAG_MIME)
    if (!widgetType) return
    const manifest = WIDGET_REGISTRY[widgetType]
    if (!manifest) return // unknown type — silently skip per spec
    const newInstance: WidgetInstance = {
      id: crypto.randomUUID(),
      widgetType,
      size: manifest.defaultSize,
      position: nextPosition(layout),
      config:
        manifest.defaultConfig && typeof manifest.defaultConfig === 'object'
          ? { ...(manifest.defaultConfig as Record<string, unknown>) }
          : ({} as Record<string, unknown>),
    }
    setLayout((prev) => [...prev, newInstance])
  }

  // -------------------------------------------------------------------------
  // Widget interactions
  // -------------------------------------------------------------------------

  function handleWidgetClick(e: React.MouseEvent<HTMLDivElement>): void {
    // The Renderer wraps each widget in a `[data-widget-id]` div. Walk up
    // from the click target to find that wrapper so config-dialog opening
    // works regardless of how deep the widget's own markup nests.
    let el: HTMLElement | null = e.target as HTMLElement
    while (el && el !== e.currentTarget) {
      const id = el.getAttribute('data-widget-id')
      if (id) {
        const instance = layout.find((w) => w.id === id)
        if (instance) setConfigInstance(instance)
        return
      }
      el = el.parentElement
    }
  }

  function handleConfigSave(next: WidgetInstance): void {
    setLayout((prev) => prev.map((w) => (w.id === next.id ? next : w)))
    setConfigInstance(null)
  }

  // -------------------------------------------------------------------------
  // CRUD actions
  // -------------------------------------------------------------------------

  function openEdit(d: Dashboard): void {
    setEditing(d)
    setLayout(d.layout ?? [])
  }

  async function saveLayout(): Promise<void> {
    try {
      if (editing) {
        const updated = await api.patch<Dashboard>(
          `/admin/dashboards/${editing.id}`,
          { layout },
        )
        toast.success('Layout saved')
        setEditing(updated)
        setLayout(updated.layout ?? layout)
        await load()
      } else {
        // No editing context — open the name prompt; the create flow takes
        // over from there and uses the current `layout` as the seed.
        setNewOpen(true)
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save layout')
    }
  }

  async function createDashboard(): Promise<void> {
    const trimmed = newName.trim()
    if (!trimmed) return
    try {
      const created = await api.post<Dashboard>('/admin/dashboards', {
        role,
        name: trimmed,
        layout,
      })
      toast.success(`Dashboard "${created.name}" created`)
      setEditing(created)
      setLayout(created.layout ?? [])
      setNewOpen(false)
      setNewName('')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create dashboard')
    }
  }

  async function setAsDefault(d: Dashboard): Promise<void> {
    try {
      await api.post(`/admin/dashboards/${d.id}/set-default`)
      toast.success(`"${d.name}" is now the default for ${d.role}`)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to set default')
    }
  }

  function confirmDelete(d: Dashboard): void {
    confirmDialog({
      message: `Permanently delete "${d.name}"? Anyone viewing this dashboard will fall back to the legacy widgets.`,
      header: 'Delete Dashboard',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/admin/dashboards/${d.id}`)
          toast.success('Dashboard deleted')
          if (editing?.id === d.id) {
            setEditing(null)
            setLayout([])
          }
          await load()
        } catch (e) {
          toast.error(
            e instanceof Error ? e.message : 'Failed to delete dashboard',
          )
        }
      },
    })
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        title="Dashboard Composer"
        description="Compose role-scoped dashboards from the widget library."
        actions={
          <>
            <Dropdown
              aria-label="Dashboard role"
              value={role}
              options={[...ROLE_OPTIONS]}
              onChange={(e: { value: DashboardRole }) => setRole(e.value)}
            />
            <Button
              label="+ New Dashboard"
              onClick={() => {
                setNewName('')
                setNewOpen(true)
              }}
            />
            <Button
              label="Save Layout"
              onClick={() => void saveLayout()}
              disabled={!editing}
            />
          </>
        }
      />

      {error && (
        <div
          role="alert"
          className="rounded bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}{' '}
          <button onClick={() => void load()} className="underline">
            Retry
          </button>
        </div>
      )}

      <div className="flex gap-4">
        <WidgetLibrarySidebar />

        <div className="flex-1 space-y-4">
          {/* Existing dashboards list */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Existing dashboards ({role})
            </h3>
            <ul className="mt-2 divide-y rounded border bg-white">
              {loading && dashboards.length === 0 && (
                <li className="p-3 text-sm text-gray-500">Loading…</li>
              )}
              {!loading && dashboards.length === 0 && (
                <li className="p-3 text-sm text-gray-500">
                  No dashboards for this role yet. Click "+ New Dashboard" to create one.
                </li>
              )}
              {dashboards.map((d) => (
                <li
                  key={d.id}
                  data-testid={`dashboard-row-${d.id}`}
                  className="flex items-center justify-between gap-2 p-3 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{d.name}</span>
                    {d.isDefault && (
                      <span
                        data-testid="default-badge"
                        className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800"
                      >
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button label="Edit" onClick={() => openEdit(d)} />
                    {!d.isDefault && (
                      <Button
                        label="Set Default"
                        onClick={() => void setAsDefault(d)}
                      />
                    )}
                    <Button label="Delete" onClick={() => confirmDelete(d)} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Composer canvas */}
          <section>
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Composer
              </h3>
              {editing && (
                <span className="text-sm text-gray-600">
                  Editing: {editing.name}
                </span>
              )}
            </div>
            <div
              data-testid="composer-dropzone"
              className="mt-2 min-h-[300px] rounded border-2 border-dashed bg-gray-50"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleWidgetClick}
            >
              {layout.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">
                  Drag a widget from the library to start composing.
                </div>
              ) : (
                <DashboardRenderer layout={layout} />
              )}
            </div>
          </section>
        </div>
      </div>

      {/* + New Dashboard prompt */}
      <Dialog
        visible={newOpen}
        onHide={() => setNewOpen(false)}
        header="New Dashboard"
        modal
        style={{ width: '24rem' }}
      >
        <div className="space-y-3 p-1 text-sm">
          <label className="flex flex-col gap-1">
            <span className="font-medium">Dashboard name</span>
            <InputText
              aria-label="Dashboard name"
              value={newName}
              onChange={(e: { target: { value: string } }) =>
                setNewName(e.target.value)
              }
              placeholder="My supervisor dashboard"
            />
          </label>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              label="Cancel"
              onClick={() => {
                setNewOpen(false)
                setNewName('')
              }}
            />
            <Button
              label="Create"
              disabled={!newName.trim()}
              onClick={() => void createDashboard()}
            />
          </div>
        </div>
      </Dialog>

      <WidgetConfigDialog
        open={configInstance !== null}
        instance={configInstance}
        onClose={() => setConfigInstance(null)}
        onSave={handleConfigSave}
      />

      <ConfirmDialog />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Public default export — RequirePermission gate
// ---------------------------------------------------------------------------

export default function DashboardComposer(): JSX.Element {
  return (
    <RequirePermission permission="dashboards:admin">
      <DashboardComposerInner />
    </RequirePermission>
  )
}
